import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, catchError, concatMap, filter, map } from 'rxjs';

import { IssuesListSettingsMessage } from '@ninety/platform/models/issues/issues-list-settings-message';
import { MeetingIssuesEvents } from '@ninety/platform/state';

import { MeetingService } from '../../_shared/services/meeting.service';

/**
 * Meeting-side handling of the Issues settings panel's save. Triggered by a shared action in
 * platform/state so neither lib imports the other. Both writes are fire-and-forget.
 */
@Injectable()
export class MeetingIssuesSettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly meetingService = inject(MeetingService);

  /** Votes per teammate lives on the meeting document, not on team settings. */
  saveVotesPerUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingIssuesEvents.settingsSaved),
        filter(({ votesPerUser }) => votesPerUser !== undefined && !!this.meetingService.currentMeeting),
        concatMap(({ votesPerUser }) =>
          // updateMeetingHttp already toasts and logs to Datadog before re-throwing. Letting that
          // escape re-reports it and tears the effect down, so swallow it here.
          this.meetingService.updateCurrentMeeting({ votesPerUser }).pipe(catchError(() => EMPTY))
        )
      ),
    { dispatch: false }
  );

  /** `broadcastMessage` no-ops outside a live meeting, so no guard is needed here. */
  broadcastIssuesSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingIssuesEvents.settingsSaved),
        map(({ teamId, issueVoting, issuesSolveRate, votesPerUser }): IssuesListSettingsMessage => {
          return {
            ...(votesPerUser !== undefined ? { votesPerUser } : {}),
            ...(issueVoting !== undefined ? { issueVoting: { teamId, value: issueVoting } } : {}),
            ...(issuesSolveRate !== undefined ? { issuesSolveRate: { teamId, ...issuesSolveRate } } : {}),
          };
        }),
        filter(document => Object.keys(document).length > 0),
        concatMap(document =>
          this.meetingService
            .broadcastMessage({ messageType: 'meeting-issues-settings', document })
            .pipe(catchError(() => EMPTY))
        )
      ),
    { dispatch: false }
  );
}
